import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Plus, Edit2, Download, FileText, FileSpreadsheet, Users, Trash2, Search, Upload, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import BulkUploadDialog from '@/components/BulkUploadDialog';
import SearchableSelect from '@/components/SearchableSelect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Positions() {
  const [positions, setPositions] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [recruiters, setRecruiters] = useState([]);
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState({});
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailPos, setDetailPos] = useState(null);
  const [user, setUser] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [filterCompany, setFilterCompany] = useState('all');
  const [filterRecruiter, setFilterRecruiter] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [jdFile, setJdFile] = useState(null);
  const [editJdFile, setEditJdFile] = useState(null);
  const [formData, setFormData] = useState({
    title: '', client_id: '', location: '', status: 'open', assigned_to: '', num_openings: 1, assigned_recruiters: [], important_pointers: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) setUser(JSON.parse(userData));
  }, []);

  const fetchPositions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/positions?status_filter=${statusFilter}`, { headers: { Authorization: `Bearer ${token}` } });
      setPositions(response.data);
    } catch (error) { toast.error('Failed to fetch positions'); }
  };

  const fetchCompanies = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/companies`, { headers: { Authorization: `Bearer ${token}` } });
      setCompanies(res.data);
    } catch (err) { console.error("./pages/Positions.jsx:", err); }
  };

  const fetchRecruiters = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } });
      // Primary assignee can be a recruiter or a team leader. Admins are excluded.
      setRecruiters(res.data.filter(u => ['recruiter', 'team_leader'].includes(u.role) && u.is_active !== false));
    } catch (err) { console.error("./pages/Positions.jsx:", err); }
  };

  useEffect(() => {
    fetchPositions();
    if (user && (user.role === 'admin' || user.role === 'team_leader')) {
      fetchCompanies();
      fetchRecruiters();
    }
  }, [user, statusFilter]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return; // Prevent double-submission
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/positions`, formData, { headers: { Authorization: `Bearer ${token}` } });
      // Upload JD if selected
      if (jdFile) {
        const fd = new FormData();
        fd.append('file', jdFile);
        await axios.post(`${API}/positions/${res.data.id}/upload-jd`, fd, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } });
      }
      toast.success('Position created');
      setOpen(false);
      setFormData({ title: '', client_id: '', location: '', status: 'open', assigned_to: '', num_openings: 1, assigned_recruiters: [], important_pointers: '' });
      setJdFile(null);
      fetchPositions();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create position'); }
    finally { setSubmitting(false); }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (editSubmitting) return; // Prevent double-submission
    setEditSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/positions/${editData.id}/edit`, editData, { headers: { Authorization: `Bearer ${token}` } });
      if (editJdFile) {
        const fd = new FormData();
        fd.append('file', editJdFile);
        await axios.post(`${API}/positions/${editData.id}/upload-jd`, fd, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } });
      }
      toast.success('Position updated');
      setEditOpen(false);
      setEditJdFile(null);
      fetchPositions();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
    finally { setEditSubmitting(false); }
  };

  const handleDeletePosition = async (id) => {
    if (!window.confirm('Delete this position?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/positions/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Position deleted');
      fetchPositions();
    } catch (error) { toast.error('Failed to delete position'); }
  };

  const handleExport = async (format) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/export/positions?format=${format}&status_filter=${statusFilter}`, {
        headers: { Authorization: `Bearer ${token}` }, responseType: 'blob'
      });
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url; link.setAttribute('download', `positions.${ext}`);
      document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (error) { toast.error('Failed to export'); }
  };

  const toggleRecruiter = (list, setList, id) => {
    if (list.includes(id)) setList(list.filter(r => r !== id));
    else setList([...list, id]);
  };

  const canCreate = user && (user.role === 'admin' || user.role === 'team_leader');
  const isAdmin = user && user.role === 'admin';
  const canExport = user && (user.role === 'admin' || user.role === 'team_leader');

  const uniqueCompanies = useMemo(() => [...new Set(positions.map(p => p.client_name).filter(Boolean))].sort(), [positions]);
  const uniqueRecruiters = useMemo(() => {
    const names = new Set();
    positions.forEach(p => {
      if (p.assigned_to_name) names.add(p.assigned_to_name);
      (p.assigned_recruiter_names || []).forEach(n => names.add(n));
    });
    return [...names].sort();
  }, [positions]);

  const filteredPositions = useMemo(() => {
    let data = [...positions];
    if (searchText) {
      const q = searchText.toLowerCase();
      data = data.filter(p => (p.title || '').toLowerCase().includes(q) || (p.client_name || '').toLowerCase().includes(q) || (p.location || '').toLowerCase().includes(q));
    }
    if (filterCompany !== 'all') data = data.filter(p => p.client_name === filterCompany);
    if (filterRecruiter !== 'all') data = data.filter(p => p.assigned_to_name === filterRecruiter || (p.assigned_recruiter_names || []).includes(filterRecruiter));
    if (filterDateFrom) data = data.filter(p => (p.created_at || '') >= filterDateFrom);
    if (filterDateTo) data = data.filter(p => (p.created_at || '').slice(0, 10) <= filterDateTo);
    return data;
  }, [positions, searchText, filterCompany, filterRecruiter, filterDateFrom, filterDateTo]);

  const clearFilters = () => { setSearchText(''); setFilterCompany('all'); setFilterRecruiter('all'); setFilterDateFrom(''); setFilterDateTo(''); };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-blue-900 mb-2" data-testid="positions-title" style={{ fontFamily: 'Manrope, sans-serif' }}>Positions</h1>
          <p className="text-slate-600">Manage job openings and assignments</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-1 bg-slate-100 rounded p-1">
            {[{ value: 'all', label: 'All' }, { value: 'open', label: 'Open' }, { value: 'closed', label: 'Closed' }].map((opt) => (
              <button key={opt.value} data-testid={`filter-${opt.value}`} onClick={() => setStatusFilter(opt.value)}
                className={`px-3 py-1.5 rounded text-xs font-bold transition-colors ${statusFilter === opt.value ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:text-blue-900'}`}>
                {opt.label}
              </button>
            ))}
          </div>
          {canExport && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="export-positions-button" variant="outline" className="border-blue-200 text-blue-700 hover:bg-blue-50"><Download size={16} className="mr-2" />Export</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem data-testid="export-positions-excel" onClick={() => handleExport('excel')}><FileSpreadsheet size={16} className="mr-2" />Excel</DropdownMenuItem>
                <DropdownMenuItem data-testid="export-positions-pdf" onClick={() => handleExport('pdf')}><FileText size={16} className="mr-2" />PDF</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {canCreate && (
            <BulkUploadDialog
              entityLabel="Positions"
              templateUrl="/templates/positions"
              uploadUrl="/positions/bulk-upload"
              templateFileName="positions_template.csv"
              helpText="Bulk-add positions from a CSV. The client must already exist in the Clients section — rows referencing an unknown client are reported as errors so you can fix and retry."
              columnSpec={[
                { name: 'title', required: true, description: 'Job title — e.g. "Senior Java Developer".' },
                { name: 'client_name', required: true, description: 'Exact (case-insensitive) name of an existing client.' },
                { name: 'location', required: false, description: 'City / Region / "Remote".' },
                { name: 'status', required: false, description: 'open | closed | on-hold. Defaults to "open".' },
                { name: 'num_openings', required: false, description: 'Number of vacancies. Defaults to 1.' },
                { name: 'important_pointers', required: false, description: 'Free-text key requirements / must-haves.' },
                { name: 'assigned_to_email', required: false, description: 'Email of a team member to assign — must exist in the tenant.' },
              ]}
              onSuccess={() => fetchPositions()}
            />
          )}
          {canCreate && (
            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setJdFile(null); }}>
              <DialogTrigger asChild>
                <Button data-testid="add-position-button" className="bg-blue-600 hover:bg-blue-700 text-white rounded font-bold"><Plus size={16} className="mr-2" />Create Position</Button>
              </DialogTrigger>
              <DialogContent className="rounded max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold text-blue-900">New Position</DialogTitle>
                  <DialogDescription>Create a new job position</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Title</Label>
                    <Input data-testid="position-title-input" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required className="mt-2 rounded border-blue-200" /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Client</Label>
                      <select data-testid="position-client-select" value={formData.client_id} onChange={(e) => setFormData({ ...formData, client_id: e.target.value })} required className="mt-2 flex h-10 w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm sr-only">
                        <option value="">Select client</option>
                        {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                      <div className="mt-2">
                        <SearchableSelect value={formData.client_id} onChange={(id) => setFormData({ ...formData, client_id: id })}
                          items={companies} emptyLabel="Select client" placeholder="Search client…" testId="position-client" />
                      </div></div>
                    <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Location</Label>
                      <Input value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} required className="mt-2 rounded border-blue-200" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">No. of Openings</Label>
                      <Input data-testid="position-openings-input" type="number" min="1" value={formData.num_openings} onChange={(e) => setFormData({ ...formData, num_openings: parseInt(e.target.value) || 1 })} className="mt-2 rounded border-blue-200" /></div>
                    <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Primary Assignee</Label>
                      <select value={formData.assigned_to} onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })} className="mt-2 flex h-10 w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm sr-only">
                        <option value="">Unassigned</option>
                        {recruiters.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                      </select>
                      <div className="mt-2">
                        <SearchableSelect value={formData.assigned_to} onChange={(id) => setFormData({ ...formData, assigned_to: id })}
                          items={recruiters} emptyLabel="Unassigned" placeholder="Search recruiter…" testId="position-assignee"
                          allowClear noneLabel="— Unassigned —" />
                      </div></div>
                  </div>
                  <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Assigned Recruiters (multiple)</Label>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {recruiters.map(r => (
                        <button key={r.id} type="button" onClick={() => toggleRecruiter(formData.assigned_recruiters, (v) => setFormData({ ...formData, assigned_recruiters: v }), r.id)}
                          className={`px-3 py-1 rounded text-xs font-bold border transition-colors ${formData.assigned_recruiters.includes(r.id) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-300 hover:border-blue-400'}`}>
                          {r.name}
                        </button>
                      ))}
                    </div></div>
                  <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Important Pointers</Label>
                    <textarea data-testid="position-pointers-input" value={formData.important_pointers} onChange={(e) => setFormData({ ...formData, important_pointers: e.target.value })} placeholder="Key requirements, must-haves, salary range, experience needed..." className="mt-2 flex w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm min-h-[80px]" /></div>
                  <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Attach JD (PDF/DOCX)</Label>
                    <Input data-testid="position-jd-upload" type="file" accept=".pdf,.docx,.doc" onChange={(e) => setJdFile(e.target.files[0] || null)} className="mt-2 rounded border-blue-200 text-sm" />
                    {jdFile && <p className="text-xs text-emerald-600 mt-1">Selected: {jdFile.name}</p>}
                  </div>
                  <Button type="submit" disabled={submitting} data-testid="submit-position-button" className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded font-bold disabled:opacity-60 disabled:cursor-not-allowed">{submitting ? 'Creating...' : 'Create Position'}</Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-blue-100 rounded shadow-sm p-4 mb-4 flex flex-wrap items-end gap-4">
        <div className="flex-1 min-w-[200px]">
          <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Search</Label>
          <div className="relative mt-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="search-positions" placeholder="Search title, client, location..." value={searchText} onChange={(e) => setSearchText(e.target.value)} className="h-9 pl-9 rounded border-blue-200 text-sm" />
          </div>
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Company</Label>
          <select data-testid="filter-position-company" value={filterCompany} onChange={(e) => setFilterCompany(e.target.value)} className="mt-1 flex h-9 rounded border border-blue-200 bg-white px-3 py-1 text-sm">
            <option value="all">All Companies</option>
            {uniqueCompanies.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Recruiter</Label>
          <select data-testid="filter-position-recruiter" value={filterRecruiter} onChange={(e) => setFilterRecruiter(e.target.value)} className="mt-1 flex h-9 rounded border border-blue-200 bg-white px-3 py-1 text-sm">
            <option value="all">All Recruiters</option>
            {uniqueRecruiters.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">From Date</Label>
          <Input data-testid="filter-position-from" type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} className="mt-1 h-9 rounded border-blue-200 text-sm" />
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">To Date</Label>
          <Input data-testid="filter-position-to" type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} className="mt-1 h-9 rounded border-blue-200 text-sm" />
        </div>
        <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-500 text-xs">Clear Filters</Button>
        <span className="text-xs text-slate-500 ml-auto">{filteredPositions.length} of {positions.length} positions</span>
      </div>

      {/* Edit Position Dialog */}
      <Dialog open={editOpen} onOpenChange={(v) => { setEditOpen(v); if (!v) setEditJdFile(null); }}>
        <DialogContent className="rounded max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-blue-900">Edit Position</DialogTitle>
            <DialogDescription>Modify position details</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4 mt-4">
            <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Title</Label>
              <Input data-testid="edit-pos-title" value={editData.title || ''} onChange={(e) => setEditData({ ...editData, title: e.target.value })} required className="mt-2 rounded border-blue-200" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Client</Label>
                <select data-testid="edit-pos-client" value={editData.client_id || ''} onChange={(e) => setEditData({ ...editData, client_id: e.target.value })} className="mt-2 flex h-10 w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm sr-only">
                  <option value="">Select client</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <div className="mt-2">
                  <SearchableSelect value={editData.client_id || ''} onChange={(id) => setEditData({ ...editData, client_id: id })}
                    items={companies} emptyLabel="Select client" placeholder="Search client…" testId="edit-pos-client-search" />
                </div></div>
              <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Location</Label>
                <Input data-testid="edit-pos-location" value={editData.location || ''} onChange={(e) => setEditData({ ...editData, location: e.target.value })} className="mt-2 rounded border-blue-200" /></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Status</Label>
                <select data-testid="edit-pos-status" value={editData.status || 'open'} onChange={(e) => setEditData({ ...editData, status: e.target.value })} className="mt-2 flex h-10 w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm">
                  <option value="open">Open</option><option value="closed">Closed</option>
                </select></div>
              <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Openings</Label>
                <Input data-testid="edit-pos-openings" type="number" min="1" value={editData.num_openings || 1} onChange={(e) => setEditData({ ...editData, num_openings: parseInt(e.target.value) || 1 })} className="mt-2 rounded border-blue-200" /></div>
              <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Primary Assignee</Label>
                <select value={editData.assigned_to || ''} onChange={(e) => setEditData({ ...editData, assigned_to: e.target.value })} className="mt-2 flex h-10 w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm sr-only">
                  <option value="">Unassigned</option>
                  {recruiters.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
                <div className="mt-2">
                  <SearchableSelect value={editData.assigned_to || ''} onChange={(id) => setEditData({ ...editData, assigned_to: id })}
                    items={recruiters} emptyLabel="Unassigned" placeholder="Search recruiter…" testId="edit-pos-assignee"
                    allowClear noneLabel="— Unassigned —" />
                </div></div>
            </div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Assigned Recruiters</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {recruiters.map(r => (
                  <button key={r.id} type="button" onClick={() => toggleRecruiter(editData.assigned_recruiters || [], (v) => setEditData({ ...editData, assigned_recruiters: v }), r.id)}
                    className={`px-3 py-1 rounded text-xs font-bold border transition-colors ${(editData.assigned_recruiters || []).includes(r.id) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-300 hover:border-blue-400'}`}>
                    {r.name}
                  </button>
                ))}
              </div></div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Important Pointers</Label>
              <textarea data-testid="edit-pos-pointers" value={editData.important_pointers || ''} onChange={(e) => setEditData({ ...editData, important_pointers: e.target.value })} placeholder="Key requirements, must-haves, salary range, experience needed..." className="mt-2 flex w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm min-h-[80px]" /></div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Upload/Replace JD (PDF/DOCX)</Label>
              <Input type="file" accept=".pdf,.docx,.doc" onChange={(e) => setEditJdFile(e.target.files[0] || null)} className="mt-2 rounded border-blue-200 text-sm" />
              {editJdFile && <p className="text-xs text-emerald-600 mt-1">New file: {editJdFile.name}</p>}
              {editData.jd_file_url && !editJdFile && <p className="text-xs text-blue-600 mt-1">Current JD: <a href={`${BACKEND_URL}${editData.jd_file_url}`} target="_blank" rel="noreferrer" className="underline">View</a></p>}
            </div>
            <Button type="submit" disabled={editSubmitting} data-testid="submit-edit-position" className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded font-bold disabled:opacity-60 disabled:cursor-not-allowed">{editSubmitting ? 'Saving...' : 'Save Changes'}</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Position Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="rounded max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-blue-900">{detailPos?.title}</DialogTitle>
            <DialogDescription>{detailPos?.client_name} — {detailPos?.location}</DialogDescription>
          </DialogHeader>
          {detailPos && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-xs font-bold uppercase text-slate-500">Status</span><p className="mt-1"><span className={`px-2 py-1 text-xs font-bold uppercase rounded ${detailPos.status === 'open' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-800'}`}>{detailPos.status}</span></p></div>
                <div><span className="text-xs font-bold uppercase text-slate-500">Openings</span><p className="mt-1 font-bold text-blue-900">{detailPos.num_openings || 1}</p></div>
              </div>
              <div><span className="text-xs font-bold uppercase text-slate-500">Assigned Recruiters</span>
                <div className="mt-1 flex flex-wrap gap-1">
                  {detailPos.assigned_to_name && <span className="bg-blue-100 text-blue-800 text-xs font-bold px-2 py-0.5 rounded">{detailPos.assigned_to_name} (Primary)</span>}
                  {(detailPos.assigned_recruiter_names || []).map((n) => <span key={n} className="bg-teal-50 text-teal-700 text-xs px-2 py-0.5 rounded border border-teal-200">{n}</span>)}
                  {!detailPos.assigned_to_name && (!detailPos.assigned_recruiter_names || detailPos.assigned_recruiter_names.length === 0) && <span className="text-slate-400 text-xs">Unassigned</span>}
                </div>
              </div>
              {detailPos.important_pointers && (
                <div><span className="text-xs font-bold uppercase text-slate-500">Important Pointers</span>
                  <div className="mt-1 bg-amber-50 border border-amber-200 rounded p-3 text-sm text-slate-700 whitespace-pre-wrap">{detailPos.important_pointers}</div>
                </div>
              )}
              {detailPos.jd_file_url && (
                <div><span className="text-xs font-bold uppercase text-slate-500">Job Description</span>
                  <a href={`${BACKEND_URL}${detailPos.jd_file_url}`} target="_blank" rel="noreferrer" className="mt-1 flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm font-medium">
                    <FileText size={16} />Download JD
                  </a>
                </div>
              )}
              <p className="text-xs text-slate-400">Created: {new Date(detailPos.created_at).toLocaleDateString()} by {detailPos.created_by_name}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <div className="bg-white border border-blue-100 rounded shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-blue-50 border-b border-blue-100">
              <TableHead className="font-bold text-blue-900">Title</TableHead>
              <TableHead className="font-bold text-blue-900">Client</TableHead>
              <TableHead className="font-bold text-blue-900">Location</TableHead>
              <TableHead className="font-bold text-blue-900 text-center">Openings</TableHead>
              <TableHead className="font-bold text-blue-900">Assigned To</TableHead>
              <TableHead className="font-bold text-blue-900">Status</TableHead>
              <TableHead className="font-bold text-blue-900">JD</TableHead>
              <TableHead className="font-bold text-blue-900">Created</TableHead>
              {canCreate && <TableHead className="font-bold text-blue-900">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredPositions.length === 0 ? (
              <TableRow><TableCell colSpan={canCreate ? 9 : 8} className="text-center text-slate-500 py-8">No positions found</TableCell></TableRow>
            ) : (
              filteredPositions.map((pos) => (
                <TableRow key={pos.id} data-testid={`position-row-${pos.id}`} className="hover:bg-blue-50">
                  <TableCell>
                    <button onClick={() => { setDetailPos(pos); setDetailOpen(true); }} className="font-semibold text-blue-900 hover:text-blue-600 hover:underline text-left">
                      {pos.title}
                    </button>
                    {pos.important_pointers && <p className="text-xs text-amber-600 mt-0.5 truncate max-w-[200px]">{pos.important_pointers}</p>}
                  </TableCell>
                  <TableCell>{pos.client_name}</TableCell>
                  <TableCell className="text-sm text-slate-600">{pos.location}</TableCell>
                  <TableCell className="text-center">
                    <span className="bg-blue-100 text-blue-800 text-xs font-bold px-2 py-0.5 rounded">{pos.num_openings || 1}</span>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {pos.assigned_to_name && <p className="font-medium text-blue-800">{pos.assigned_to_name}</p>}
                      {pos.assigned_recruiter_names?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {pos.assigned_recruiter_names.map((name) => (
                            <span key={name} className="bg-teal-50 text-teal-700 text-xs px-1.5 py-0.5 rounded border border-teal-200">{name}</span>
                          ))}
                        </div>
                      )}
                      {!pos.assigned_to_name && (!pos.assigned_recruiter_names || pos.assigned_recruiter_names.length === 0) && (
                        <span className="text-slate-400 text-xs">Unassigned</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 text-xs font-bold uppercase rounded ${pos.status === 'open' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-800'}`}>{pos.status}</span>
                  </TableCell>
                  <TableCell>
                    {pos.jd_file_url ? (
                      <a href={`${BACKEND_URL}${pos.jd_file_url}`} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-800" title="View JD"><FileText size={16} /></a>
                    ) : <span className="text-slate-300 text-xs">-</span>}
                  </TableCell>
                  <TableCell className="text-sm text-slate-500">{new Date(pos.created_at).toLocaleDateString()}</TableCell>
                  {canCreate && (
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" onClick={() => { setDetailPos(pos); setDetailOpen(true); }} className="text-slate-600 hover:text-blue-900 hover:bg-blue-50" title="View Details"><Eye size={14} /></Button>
                        {isAdmin && (
                          <Button size="sm" variant="outline" data-testid={`edit-position-${pos.id}`}
                            onClick={() => { setEditData({ id: pos.id, title: pos.title, client_id: pos.client_id, location: pos.location, status: pos.status, num_openings: pos.num_openings || 1, assigned_to: pos.assigned_to || '', assigned_recruiters: pos.assigned_recruiters || [], important_pointers: pos.important_pointers || '', jd_file_url: pos.jd_file_url }); setEditOpen(true); }}
                            className="text-blue-600 border-blue-200 hover:bg-blue-50 text-xs font-bold">
                            <Edit2 size={13} className="mr-1" />Edit
                          </Button>
                        )}
                        {isAdmin && (
                          <Button size="sm" variant="ghost" data-testid={`delete-position-${pos.id}`} onClick={() => handleDeletePosition(pos.id)} className="text-red-600 hover:text-red-800 hover:bg-red-50"><Trash2 size={14} /></Button>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
